// root '../../../';

// dependencies
import { Child } from 'pym.js';
import { select, selectAll, event as d3Event } from 'd3-selection';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';

// styles
import './styles/graphic.scss';

// Store current filter and data
let currentFilter = 'all';
let showAll = false;
const INITIAL_DISPLAY_COUNT = 10; // Show most recent 10 attacks initially

const appData = {
  strikesData: null,
  metadata: null,
};

window.pymChild = new Child();

// Format date helper
const formatDate = timeFormat('%B %-d, %Y');
const formatDateShort = timeFormat('%b %-d');

// Parse date string as local date (not UTC) to avoid timezone issues
function parseLocalDate(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function draw() {
  const timeline = select('#graphic').html('');

  if (!appData.strikesData) {
    timeline.append('p').text('Loading...');
    return;
  }

  // Theater filters
  const theaters = ['all', 'CARIBBEAN', 'PACIFIC'];

  // Filter strikes first so we can calculate summary
  const filteredStrikes = appData.strikesData
    .filter((strike) => {
      if (currentFilter === 'all') return true;

      const theaterValue = strike.theater || '';

      // If theater is empty, only show in 'all' filter
      if (!theaterValue.trim()) return false;

      // Check if the theater string contains the current filter
      // This handles cases like "CARIBBEAN and PACIFIC" matching "CARIBBEAN" filter
      return theaterValue.toUpperCase().includes(currentFilter);
    })
    .sort((a, b) =>
      // Sort by date descending (newest first)
      new Date(b.date) - new Date(a.date));

  // Get pre-formatted last strike date from metadata
  const lastStrikeDateStr = appData.metadata && appData.metadata.lastAttackStr
    ? appData.metadata.lastAttackStr
    : null;

  // Calculate summary stats for filtered data
  if (filteredStrikes.length > 0 && appData.metadata) {
    const totalAttacks = filteredStrikes.reduce((sum, strike) => sum + strike.attacks, 0);
    const totalDeaths = filteredStrikes.reduce((sum, strike) => sum + strike.deaths, 0);
    const totalSurvivors = filteredStrikes.reduce((sum, strike) => sum + (strike.survivors || 0), 0);

    const summary = timeline.append('div')
      .attr('class', 'summary-box');

    let summaryText = `As of ${lastStrikeDateStr || appData.metadata.last_updated}, there have been at least <span class='highlight'>${totalAttacks} ${totalAttacks === 1 ? 'attack' : 'attacks'}</span> resulting in <span class='highlight'>${totalDeaths} ${totalDeaths === 1 ? 'death' : 'deaths'}</span>`;

    // if (totalSurvivors > 0) {
    //   summaryText += ` and <span class='highlight'>${totalSurvivors} ${totalSurvivors === 1 ? 'survivor' : 'survivors'}</span>`;
    // }

    summaryText += '.';

    if (currentFilter !== 'all') {
      summaryText += ` <span class="theater-label">(${currentFilter.charAt(0) + currentFilter.slice(1).toLowerCase()} theater only)</span>`;
    }

    summary.append('p')
      .attr('class', 'summary-text')
      .html(summaryText);
  }

  // Add filter buttons
  const filterContainer = timeline.append('div')
    .attr('class', 'filter-container');

  filterContainer.append('span')
    .attr('class', 'filter-label')
    .text('Theater: ');

  const filters = filterContainer.selectAll('.filter-btn')
    .data(theaters)
    .enter()
    .append('button')
    .attr('class', d => `filter-btn ${d === currentFilter ? 'active' : ''}`)
    .attr('data-theater', d => d)
    .text((d) => {
      if (d === 'all') return 'All';
      return d.charAt(0) + d.slice(1).toLowerCase();
    })
    .on('click', function () {
      currentFilter = this.getAttribute('data-theater');
      draw();
      window.pymChild.sendHeight();
      // Re-trigger animation check after redraw
      if (typeof checkAndAnimateEntries === 'function') {
        checkAndAnimateEntries();
      }
    });

  // Determine how many strikes to show
  const strikesToShow = showAll ? filteredStrikes : filteredStrikes.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = filteredStrikes.length > INITIAL_DISPLAY_COUNT;

  // Render strikes (already filtered above for summary)
  strikesToShow.forEach((strike) => {
    const theaterValue = strike.theater || '';

    // Split theater string by "and" to handle multiple theaters
    const theaters = theaterValue.trim()
      ? theaterValue.split(/\s+and\s+/i).map(t => t.trim())
      : ['Unknown'];

    const entry = timeline.append('div')
      .attr('class', 'timeline-entry')
      .attr('data-theater', theaterValue || 'UNKNOWN');

    // Header with date
    const headerContainer = entry.append('div')
      .attr('class', 'timeline-header-container');

    const dateStr = strike.date_unclear
      ? `Approximate date: ${formatDate(parseLocalDate(strike.date))}`
      : formatDate(parseLocalDate(strike.date));

    headerContainer.append('h3')
      .attr('class', 'timeline-header')
      .text(dateStr);

    // Theater badge(s) - create one for each theater
    theaters.forEach((theater) => {
      const theaterDisplay = theater.charAt(0) + theater.slice(1).toLowerCase();
      headerContainer.append('span')
        .attr('class', 'theater-tag')
        .attr('data-theater', theater.toUpperCase())
        .text(theaterDisplay);
    });

    // Alleged target and casualty info on separate lines
    const targetCasualtyContainer = entry.append('div')
      .attr('class', 'target-casualty-line');

    // Alleged target line
    if (strike.alleged_targets) {
      targetCasualtyContainer.append('p')
        .html(`<strong>Alleged target:</strong> ${strike.alleged_targets}`);
    }

    // Impact line with casualty count and vessel info
    let casualtyText = '<strong>Impact:</strong> ';
    if (strike.attacks > 1) {
      casualtyText += `${strike.attacks} attacks, `;
    }
    casualtyText += `${strike.deaths} ${strike.deaths === 1 ? 'death' : 'deaths'}`;
    if (strike.survivors) {
      casualtyText += `, ${strike.survivors} ${strike.survivors === 1 ? 'survivor' : 'survivors'}`;
    }
    if (strike.vessel_count && strike.vessel_type) {
      casualtyText += ` on ${strike.vessel_count} ${strike.vessel_type}`;
    }

    targetCasualtyContainer.append('p')
      .html(casualtyText);

    // Image if available
    if (strike.image) {
      const figure = entry.append('figure');
      figure.append('img')
        .attr('src', strike.image)
        .attr('alt', `Strike imagery from ${dateStr}`)
        .on('load', () => {
          // Recalculate height after image loads
          window.pymChild.sendHeight();
        });

      let captionSource = strike.source;
      if (strike.source === '@realDonaldTrump') {
        captionSource = 'President Donald Trump';
      } else if (strike.source === '@secwar') {
        captionSource = 'Defense Secretary Pete Hegseth';
      } else if (strike.source === '@southcom') {
        captionSource = 'US Southern Command';
      }

      figure.append('figcaption')
        .html(`Image from ${captionSource}'s announcement`);
    }

    // CNN reporting section
    if (strike.cnn_reporting) {
      const reportingSection = entry.append('div')
        .attr('class', 'cnn-reporting');

      reportingSection.append('h4')
        .text('CNN reporting:');

      const reportingText = reportingSection.append('div')
        .attr('class', 'reporting-text');

      // Split text into paragraphs by double newlines
      const paragraphs = strike.cnn_reporting.split(/\n\n+/).filter(para => para.trim());

      const MAX_INITIAL_PARAGRAPHS = 4;
      const hasMoreParagraphs = paragraphs.length > MAX_INITIAL_PARAGRAPHS;

      paragraphs.forEach((para, index) => {
        if (para.trim()) {
          const p = reportingText.append('p').text(para.trim());

          // Hide paragraphs beyond the initial count if there are many
          if (hasMoreParagraphs && index >= MAX_INITIAL_PARAGRAPHS) {
            p.attr('class', 'extra-paragraph hidden');
          }
        }
      });

      // Add "Read more" button if there are many paragraphs
      if (hasMoreParagraphs) {
        const readMoreBtn = reportingSection.append('button')
          .attr('class', 'read-more-btn')
          .text('Read more')
          .on('click', function () {
            const btn = select(this);
            const extraParas = reportingSection.selectAll('.extra-paragraph');
            const isExpanded = btn.classed('expanded');

            if (isExpanded) {
              extraParas.classed('hidden', true);
              btn.text('Read more').classed('expanded', false);
            } else {
              extraParas.classed('hidden', false);
              btn.text('Read less').classed('expanded', true);
            }

            // Update iframe height after expansion/collapse
            window.pymChild.sendHeight();
          });
      }
    }

    // Details section (below image)
    const details = entry.append('div')
      .attr('class', 'strike-details');

    // Show additional details note for the Oct 27 multiple attacks entry
    if (strike.date === '2025-10-27' && strike.attacks > 1) {
      details.append('p')
        .html('<strong>Additional details:</strong> Three attacks on 4 boats.');
    }

    // Source (link or text)
    const sourceP = details.append('p')
      .attr('class', 'source-link');

    sourceP.append('strong').text('Strike announcement: ');

    // Determine source text based on the source field
    let sourceText;
    if (strike.source === '@realDonaldTrump') {
      sourceText = 'President Trump';
    } else if (strike.source === '@secwar') {
      sourceText = 'Defense Secretary';
    } else if (strike.source === '@southcom') {
      sourceText = 'US Southern Command';
    } else {
      // Use the source field as-is (e.g., "Defense Department official")
      sourceText = strike.source;
    }

    if (strike.post_link) {
      sourceP.append('a')
        .attr('href', strike.post_link)
        .attr('target', '_blank')
        .text(sourceText);
    } else {
      sourceP.append('span').text(sourceText);
    }
  });

  // Add "Show more" / "Show less" button if there are more strikes
  if (hasMore) {
    const showMoreContainer = timeline.append('div')
      .attr('class', 'show-more-container');

    showMoreContainer.append('button')
      .attr('class', 'show-more-btn')
      .text(showAll ? 'View fewer attacks' : 'View all attacks')
      .on('click', () => {
        showAll = !showAll;
        draw();
        window.pymChild.sendHeight();
        // Re-trigger animation check after redraw
        if (typeof checkAndAnimateEntries === 'function') {
          checkAndAnimateEntries();
        }
      });
  }

  // Add last updated info
  if (appData.metadata) {
    const lastUpdatedDiv = timeline.append('div')
      .attr('class', 'last-updated');

    lastUpdatedDiv.append('p')
      .text(`Last updated: ${appData.metadata.lastUpdatedStr}. Date of last known attack: ${lastStrikeDateStr}`);
  }

  // Add byline
  const byline = timeline.append('div')
    .attr('class', 'byline');

  byline.append('p')
    .text('Graphic: Matt Stiles, Avery Schmitz and Michael Rios, CNN');

  // Send height after initial render
  window.pymChild.sendHeight();
}

// Pym scroll-based animation for timeline entries
// This tracks the iframe's position in the PARENT page viewport,
// so scroll animations work correctly when embedded in articles

let isEmbedded = false; // Track if we're in an embedded context

function checkVisibleEntries(positionString) {
  // Parse the position string: "scrollTop viewportHeight iframeTop iframeBottom"
  const positions = positionString.split(' ').map(parseFloat);
  const scrollTop = positions[0];
  const viewportHeight = positions[1];
  const iframeTop = positions[2];
  const iframeBottom = positions[3];

  const entries = document.querySelectorAll('.timeline-entry');

  entries.forEach((entry) => {
    const rect = entry.getBoundingClientRect();
    // Element's position relative to the parent page
    const entryTop = rect.top + iframeTop;

    // Calculate visibility ratio (similar to the example code)
    // Positive values mean element is in or approaching viewport
    const distanceFromViewportTop = entryTop - scrollTop;
    const visibilityRatio = 1 - (distanceFromViewportTop / viewportHeight);

    // Element is "in view" if ratio > 0.1 (appears when ~10% in viewport)
    // This provides a nice buffer so animation starts before fully visible
    if (visibilityRatio > 0.1 && !entry.classList.contains('is-visible')) {
      entry.classList.add('is-visible');
    }
  });
}

// IntersectionObserver for standalone viewing
let observer = null;

function setupStandaloneScrollAnimation() {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  });

  // Observe all timeline entries
  document.querySelectorAll('.timeline-entry').forEach((entry) => {
    observer.observe(entry);
  });
}

// Function to check entries after filter changes
function checkAndAnimateEntries() {
  if (!isEmbedded) {
    // Not embedded - re-setup IntersectionObserver for new entries
    if (observer) {
      observer.disconnect();
    }
    setupStandaloneScrollAnimation();
  } else {
    // If embedded, request current position from parent to trigger check
    window.pymChild.sendMessage('request-viewport-iframe-position');
  }
}

// Fetch data from S3
async function loadData() {
  try {
    console.log('Fetching data...');
    const response = await fetch('https://ix.cnn.io/data/drug-boats/drug_boat_strikes_bot.json');
    console.log('Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    console.log('Data loaded:', json);

    appData.strikesData = json.data;
    appData.metadata = json.metadata;

    draw();
    window.pymChild.sendHeight();

    // Initial animation check
    setTimeout(() => {
      checkAndAnimateEntries();
    }, 100);
  } catch (error) {
    console.error('Error loading strike data:', error);
    select('#graphic').html(`<p style="color: #c52622;">Error loading data: ${error.message}. Please refresh the page.</p>`);
  }
}

// Set up Pym scroll listener after pymChild is initialized
window.onload = () => {
  const { pymChild } = window;

  // Load data
  loadData();

  // Listen for scroll position updates from parent page
  pymChild.onMessage('viewport-iframe-position', (position) => {
    isEmbedded = true;
    checkVisibleEntries(position);
  });

  // Fallback for standalone viewing (not in iframe)
  // After a short delay, check if we're getting Pym messages
  setTimeout(() => {
    if (!isEmbedded) {
      // Not embedded - use IntersectionObserver for scroll animation
      setupStandaloneScrollAnimation();
    }
  }, 100);
};
